import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { pulsesApi, MOOD_PRESETS } from "../../lib/pulses";
import { formatApiError } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";

function TypeButton({ id, label, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={testId}
      className={
        "px-3 py-2 rounded-xl text-xs font-display uppercase tracking-widest border transition-colors " +
        (selected
          ? "q-accent-bg text-[color:var(--accent-contrast)] border-transparent"
          : "bg-black/30 text-slate-300 border-white/10 hover:border-[color:var(--accent)]")
      }
    >
      {label}
    </button>
  );
}

export function PulseComposer({ onCreated }) {
  const { mode, isNegocio } = useTheme();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Type — mode-scoped
  const generalTypes = [
    { id: "poll", label: "Encuesta" },
    { id: "mood", label: "Estado" },
    { id: "text", label: "Texto" },
  ];
  const [type, setType] = useState("poll");

  // Poll
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);

  // Mood
  const [mood, setMood] = useState(MOOD_PRESETS[0]);

  // Text
  const [text, setText] = useState("");

  // Offer
  const [business, setBusiness] = useState("");
  const [description, setDescription] = useState("");
  const [discount, setDiscount] = useState(20);

  const resetForm = () => {
    setQuestion("");
    setOptions(["", ""]);
    setMood(MOOD_PRESETS[0]);
    setText("");
    setBusiness("");
    setDescription("");
    setDiscount(20);
  };

  const submit = async () => {
    setBusy(true);
    try {
      let payload;
      if (isNegocio) {
        payload = {
          type: "offer",
          mode: "negocio",
          business: business.trim(),
          description: description.trim(),
          discount_pct: Number(discount),
        };
      } else if (type === "poll") {
        payload = {
          type: "poll",
          mode: "general",
          question: question.trim(),
          options: options.map((o) => o.trim()).filter(Boolean),
        };
      } else if (type === "mood") {
        payload = { type: "mood", mode: "general", mood_preset: mood };
      } else {
        payload = { type: "text", mode: "general", text: text.trim() };
      }
      const created = await pulsesApi.create(payload);
      toast.success("Pulso publicado.");
      onCreated && onCreated(created);
      resetForm();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="q-btn-primary inline-flex items-center gap-2"
          data-testid="publish-pulse-btn"
        >
          <Sparkles size={16} /> Publicar OndaPulse
        </button>
      </DialogTrigger>
      <DialogContent
        className="border-white/10 bg-[#0f172a]/95 backdrop-blur-2xl text-slate-100 max-w-lg"
        data-testid="pulse-composer-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isNegocio ? "Nueva Flash Offer" : "Publicar OndaPulse"}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Modo activo: <span className="q-accent-text font-mono">{mode}</span> · Expira en 24 h.
          </DialogDescription>
        </DialogHeader>

        {!isNegocio && (
          <div className="flex flex-wrap gap-2">
            {generalTypes.map((t) => (
              <TypeButton
                key={t.id}
                id={t.id}
                label={t.label}
                selected={type === t.id}
                onClick={setType}
                testId={`composer-type-${t.id}`}
              />
            ))}
          </div>
        )}

        <div className="space-y-3">
          {isNegocio ? (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Nombre del negocio
                </span>
                <input
                  className="q-input mt-1"
                  data-testid="composer-offer-business"
                  value={business}
                  onChange={(e) => setBusiness(e.target.value)}
                  maxLength={80}
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Descripción de la promo
                </span>
                <textarea
                  className="q-input mt-1 min-h-[72px] resize-none"
                  data-testid="composer-offer-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={200}
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Descuento (%)
                </span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  className="q-input mt-1"
                  data-testid="composer-offer-discount"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </label>
            </>
          ) : type === "poll" ? (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Pregunta
                </span>
                <input
                  className="q-input mt-1"
                  data-testid="composer-poll-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  maxLength={140}
                />
              </label>
              <div className="space-y-2">
                <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                  Opciones (2 a 4)
                </span>
                {options.map((o, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className="q-input"
                      data-testid={`composer-poll-option-${i}`}
                      placeholder={`Opción ${i + 1}`}
                      value={o}
                      onChange={(e) => {
                        const next = [...options];
                        next[i] = e.target.value;
                        setOptions(next);
                      }}
                      maxLength={50}
                    />
                    {options.length > 2 && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-rose-300 p-2"
                        onClick={() => setOptions(options.filter((_, idx) => idx !== i))}
                        aria-label={`Quitar opción ${i + 1}`}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                {options.length < 4 && (
                  <button
                    type="button"
                    className="q-btn-ghost inline-flex items-center gap-1 !py-1.5 !text-xs"
                    onClick={() => setOptions([...options, ""])}
                    data-testid="composer-poll-add-option"
                  >
                    <Plus size={14} /> Añadir opción
                  </button>
                )}
              </div>
            </>
          ) : type === "mood" ? (
            <div>
              <span className="text-xs uppercase tracking-widest text-slate-400 font-display block mb-2">
                Estado
              </span>
              <div className="grid grid-cols-2 gap-2">
                {MOOD_PRESETS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMood(m)}
                    data-testid={`composer-mood-preset-${m}`}
                    className={
                      "rounded-xl border px-3 py-2 text-sm font-display transition-colors " +
                      (mood === m
                        ? "q-accent-bg text-[color:var(--accent-contrast)] border-transparent"
                        : "bg-black/30 text-slate-200 border-white/10 hover:border-[color:var(--accent)]")
                    }
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-display">
                Texto ({text.length}/140)
              </span>
              <textarea
                className="q-input mt-1 min-h-[80px] resize-none"
                data-testid="composer-text-body"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 140))}
                maxLength={140}
              />
            </label>
          )}
        </div>

        <DialogFooter>
          <button
            className="q-btn-ghost"
            onClick={() => setOpen(false)}
            disabled={busy}
            type="button"
          >
            Cancelar
          </button>
          <button
            className="q-btn-primary inline-flex items-center gap-2"
            onClick={submit}
            disabled={busy}
            data-testid="composer-submit"
            type="button"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Publicar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
